// EmptyState — placeholder для пустых списков.
// Иконка (Material Symbol) + сообщение + опциональная action-кнопка.
import type { ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';

interface EmptyStateProps {
  /** Заголовок (по умолчанию "Нет данных"). */
  title?: string;
  /** Описание под заголовком. */
  description?: string;
  /** Текст кнопки действия. */
  actionLabel?: string;
  /** Обработчик клика по кнопке действия. */
  onAction?: () => void;
  /** Кастомная иконка (по умолчанию Inbox). */
  icon?: ReactNode;
}

/** Универсальный placeholder для пустых списков. */
export function EmptyState({
  title = 'Нет данных',
  description,
  actionLabel,
  onAction,
  icon,
}: EmptyStateProps): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        py: 6,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box sx={{ color: 'text.secondary', fontSize: 64, lineHeight: 1 }}>
        {icon ?? <InboxIcon sx={{ fontSize: 'inherit' }} />}
      </Box>
      <Typography variant="h6" component="p" color="text.primary">
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
          {description}
        </Typography>
      ) : null}
      {actionLabel && onAction ? (
        <Button variant="contained" onClick={onAction} sx={{ mt: 1 }}>
          {actionLabel}
        </Button>
      ) : null}
    </Box>
  );
}
